import { useState, useRef, useEffect, useCallback } from 'react'
import { Button, Modal, Input, Badge, toast } from '@atrvd/saas-ui'
import { Building2, Loader2, Search, Check, XCircle } from 'lucide-react'
import { useMFA } from '@/hooks/useMFA'
import {
  useSearchLedgerSyncInstitutions,
  useCreateLedgerSyncWidget,
  type LedgerSyncInstitution,
} from '@/hooks/useLedgerSync'
import { useOrganization } from '@/contexts/OrganizationContext'
import { lsDebug } from '@/lib/ledgersync-debug'
import { ManualBankFormDialog } from '@/components/manual-banks/ManualBankFormDialog'
import {
  financialAccountsBasePath,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import { ReadOnlyTooltip } from '@/components/financial-accounts/ReadOnlyControls'

/** Props for the LedgerSyncConnectButton component. */
interface LedgerSyncConnectButtonProps {
  /**
   * The Financial Accounts page the button is on: a facility's (null = the
   * organization-wide one). The user returns there from the hosted page, the
   * manual fallback pre-selects the facility, and an inactive facility's
   * read-only page disables the button.
   */
  scope: FacilityFinancialAccountsScope | null
  /** Admin context: overrides the current organization when provided. */
  organizationId?: string
  onError?: (error: Error) => void
}

/** Shown under the search when Continue is pressed with nothing picked. */
const NO_INSTITUTION_ERROR = 'Select a financial institution to continue'

/** Shown once the widget session exists and the browser is being sent away. */
const REDIRECTING_MESSAGE = 'Taking you to the secure LedgerSync authorization page.'

/** Debounce delay (ms) before a search keystroke fires a catalog query. */
const SEARCH_DEBOUNCE_MS = 350

/**
 * LedgerSync v3 connect entry point (DCP-216; org-level since DCP-324).
 *
 * v3 requires the user to pick an institution BEFORE the hosted widget opens
 * (v2 launched the widget directly). Clicking "Connect Bank" opens a dialog
 * with a bank search: typing queries the v3 institution catalog, the user
 * selects a result, and confirming creates a widget session and redirects the
 * browser to the hosted Connect widget. The connection is finalized by the
 * connection-status poll on the callback page and by the page-level
 * reconciliation; webhook delivery is a secondary channel only.
 */
export function LedgerSyncConnectButton({
  scope,
  organizationId: propOrgId,
  onError,
}: LedgerSyncConnectButtonProps) {
  const { currentOrganization } = useOrganization()
  const { hasMFA } = useMFA()
  const search = useSearchLedgerSyncInstitutions()
  const createWidget = useCreateLedgerSyncWidget()

  const effectiveOrgId = propOrgId || currentOrganization?.id
  const readOnly = !!scope?.readOnly

  // Dialog + search state.
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<LedgerSyncInstitution | null>(null)
  // DCP-263: tracks the trimmed query a search has actually fired for (set
  // when the debounce timer runs), so the "no results" fallback can be
  // distinguished from the ~350ms debounce window where `results` is still
  // stale from a previous query.
  const [searchedQuery, setSearchedQuery] = useState('')
  // DCP-263: controls the manual "Add Financial Account" dialog opened as a fallback when
  // the bank search returns zero results for a typed name.
  const [manualDialogOpen, setManualDialogOpen] = useState(false)
  /** Continue was pressed with no institution picked; cleared by picking one. */
  const [showNoInstitutionError, setShowNoInstitutionError] = useState(false)
  /** The widget URL is in hand and the browser is navigating to it. */
  const [redirecting, setRedirecting] = useState(false)
  // Debounce timer for keystroke-driven catalog searches.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { mutate: runSearch, reset: resetSearch } = search

  // Reset all transient dialog state. Used by open/close transitions (AC.5).
  const resetState = useCallback(() => {
    setQuery('')
    setSelected(null)
    setSearchedQuery('')
    setShowNoInstitutionError(false)
    setRedirecting(false)
    resetSearch()
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [resetSearch])

  // Clear any pending debounce timer on unmount to avoid a stray search.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Back from the hosted page can restore this page from the bfcache with its
  // state frozen mid-redirect; without this, Continue would stay disabled.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setRedirecting(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  // Open the dialog from a clean slate.
  const handleOpen = () => {
    resetState()
    setOpen(true)
  }

  // Cancel / close without saving — discards search + selection (AC.5).
  const handleClose = () => {
    setOpen(false)
    resetState()
  }

  // Debounced search: re-query the catalog as the user types.
  const handleQueryChange = (value: string) => {
    setQuery(value)
    // Typing invalidates any prior selection so submit reflects current intent.
    setSelected(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const trimmed = value.trim()
    if (trimmed.length === 0) {
      setSearchedQuery('')
      resetSearch()
      return
    }
    debounceRef.current = setTimeout(() => {
      // Mark this query as "searched" at the moment the debounced search
      // actually fires — this is what gates the no-results CTA below.
      setSearchedQuery(trimmed)
      runSearch(trimmed)
    }, SEARCH_DEBOUNCE_MS)
  }

  // DCP-263: close the LedgerSync search modal and open the manual Add
  // Financial Account dialog, pre-filled with the typed bank name. The
  // saas-ui Modal stacks at a fixed z-index and each instance attaches its
  // own Escape listener, so we SWAP modals (close one, then open the other)
  // rather than keep both open at once.
  const handleAddManually = () => {
    setOpen(false)
    setManualDialogOpen(true)
  }

  // Confirm: create a widget session for the chosen institution, then redirect.
  const handleConnect = () => {
    // Continue stays enabled; with nothing picked it explains instead.
    if (!selected) {
      setShowNoInstitutionError(true)
      return
    }
    // debug: bank-connection flow trace (DCP-216)
    lsDebug('connect:institution-selected', { institutionId: selected.id, source: selected.source })
    createWidget.mutate(
      // No facility is sent: `create-widget` is org-level since DCP-324.
      {
        institutionId: selected.id,
        organizationId: effectiveOrgId,
        // Back to the page the user started from, the facility's included.
        returnPath: financialAccountsBasePath(scope),
      },
      {
        onSuccess: ({ widgetUrl, connectionId }) => {
          if (!widgetUrl) {
            // The async operation has not yet produced a widget URL; the
            // connection-status poll will advance it once the backend is ready.
            // Tell the user to retry so they can re-enter the dialog.
            toast.error('The connection is still being prepared. Please try again in a moment.')
            return
          }
          // debug: bank-connection flow trace (DCP-216)
          lsDebug('connect:redirect-to-widget', { connectionId, hasWidgetUrl: !!widgetUrl })
          // Redirect to the hosted Connect widget (state already persisted).
          setRedirecting(true)
          window.location.href = widgetUrl
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to start the connection')
          onError?.(error as Error)
        },
      },
    )
  }

  const results = search.data ?? []
  const isSearching = search.isPending
  const isSubmitting = createWidget.isPending || redirecting
  const trimmedQuery = query.trim()
  // A debounced search is still pending (or hasn't fired yet) when the
  // trimmed query hasn't been recorded as "searched" — avoids flashing the
  // no-results CTA mid-keystroke.
  const isDebouncePending = trimmedQuery.length > 0 && searchedQuery !== trimmedQuery
  // Genuine no-results: a search for the CURRENT query has completed, found
  // nothing, and isn't in an error state.
  const showNoResultsFallback =
    !isSearching &&
    !search.isError &&
    !isDebouncePending &&
    results.length === 0 &&
    trimmedQuery.length > 0 &&
    searchedQuery === trimmedQuery

  return (
    <>
      <div className="flex flex-col items-end gap-1">
        <ReadOnlyTooltip readOnly={readOnly}>
          <Button onClick={handleOpen} disabled={!hasMFA || readOnly} className="gap-2">
            <Building2 className="h-4 w-4" />
            Connect Financial Account
          </Button>
        </ReadOnlyTooltip>
        {!hasMFA && <p className="text-xs text-muted-foreground">Enable 2FA to connect financial accounts</p>}
      </div>

      <Modal
        isOpen={open}
        onClose={handleClose}
        title="Connect a Financial Account"
        description="Search for your bank or card issuer, then continue to the secure connection widget."
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleConnect} disabled={isSubmitting} className="gap-2">
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Continue
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {/* Institution search input */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder="Search for your institution..."
              className="pl-9"
              aria-label="Search for your institution"
              aria-invalid={showNoInstitutionError || undefined}
              aria-describedby={showNoInstitutionError ? 'connect-institution-error' : undefined}
            />
          </div>
          {showNoInstitutionError && (
            <p id="connect-institution-error" className="text-sm text-destructive">
              {NO_INSTITUTION_ERROR}
            </p>
          )}

          {/* Results / states */}
          <div className="max-h-72 overflow-y-auto rounded-md border">
            {isSearching || isDebouncePending ? (
              <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching…
              </div>
            ) : search.isError ? (
              <div className="p-6 text-center text-sm text-destructive">
                {search.error instanceof Error ? search.error.message : 'Search failed. Try again.'}
              </div>
            ) : showNoResultsFallback ? (
              // DCP-263: bank not found in the LedgerSync catalog — offer a
              // fallback to manually add the bank, pre-filled with the name
              // the user already typed. Layout mirrors the Figma empty-state:
              // an error glyph, the message, and an outlined "Add manually" CTA,
              // vertically centered in the results area.
              <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 p-6 text-center text-sm">
                {/* Error glyph on a soft red disc (matches Figma empty-state). */}
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                  <XCircle className="h-6 w-6 text-destructive" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    No institutions found for &quot;{trimmedQuery}&quot;
                  </p>
                  <p className="text-muted-foreground">
                    Try a different name, or add this financial account manually.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddManually}
                  className="gap-2 text-primary hover:text-primary"
                >
                  + Add Financial Account
                </Button>
              </div>
            ) : results.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                Start typing to search for your bank or card issuer.
              </div>
            ) : (
              <ul className="divide-y">
                {results.map((inst) => {
                  const isSelected = selected?.id === inst.id
                  return (
                    <li key={inst.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(inst)
                          setShowNoInstitutionError(false)
                        }}
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                          isSelected ? 'bg-muted' : ''
                        }`}
                        aria-pressed={isSelected}
                      >
                        <span className="flex items-center gap-2 font-medium">
                          {isSelected ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          )}
                          {inst.name}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5">
                          {inst.source && (
                            <Badge variant="secondary" className="uppercase">
                              {inst.source}
                            </Badge>
                          )}
                          {inst.oauthSupported && <Badge variant="outline">OAuth</Badge>}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
          {isSubmitting && (
            <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
              {REDIRECTING_MESSAGE}
            </p>
          )}
        </div>
      </Modal>

      {/* Manual fallback, opened when the search finds no match: the Add
          Financial Account form, prefilled with the name the user searched for. */}
      {manualDialogOpen && (
        <ManualBankFormDialog
          mode={{ kind: 'create', initialName: trimmedQuery, defaultFacilityId: scope?.facilityId ?? null }}
          onClose={() => setManualDialogOpen(false)}
        />
      )}
    </>
  )
}
